import { useState } from "react";

import { Button } from "@/components/button";

import { OnboardingProgressBar } from "./components/OnboardingProgressBar";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const DURATIONS = [
  { label: "30 min", value: 30 },
  { label: "45 min", value: 45 },
  { label: "60 min", value: 60 },
];

const AVAILABLE_TIMES = ["10:00", "15:00", "15:15"];

interface CalendarProps {
  currentMonth: Date;
  onDateSelect: (date: Date) => void;
  onMonthChange: (month: Date) => void;
  selectedDate: Date | null;
}

function Calendar({
  currentMonth,
  onDateSelect,
  onMonthChange,
  selectedDate,
}: CalendarProps) {
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();

  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const firstDayWeekday = firstDayOfMonth.getDay();
  const daysInMonth = lastDayOfMonth.getDate();

  const days = [];

  // Empty cells for days before the first day of the month
  for (let i = 0; i < firstDayWeekday; i++) {
    days.push(null);
  }

  // Days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(new Date(year, month, day));
  }

  const goToPreviousMonth = () => {
    onMonthChange(new Date(year, month - 1, 1));
  };

  const goToNextMonth = () => {
    onMonthChange(new Date(year, month + 1, 1));
  };

  const isToday = (date: Date) => {
    const today = new Date();

    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date: Date) =>
    selectedDate && date.toDateString() === selectedDate.toDateString();

  return (
    <div className="bg-white rounded-2xl border border-border-secondary p-6 h-full flex flex-col">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          className="p-2 hover:bg-bg-secondary rounded-lg"
          onClick={goToPreviousMonth}
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              d="M15 19l-7-7 7-7"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
            />
          </svg>
        </button>

        <h3 className="text-lg font-semibold text-text-primary">
          {MONTHS[month]} {year}
        </h3>

        <button
          className="p-2 hover:bg-bg-secondary rounded-lg"
          onClick={goToNextMonth}
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              d="M9 5l7 7-7 7"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
            />
          </svg>
        </button>
      </div>

      {/* Weekday Headers */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {WEEKDAYS.map((day) => (
          <div
            className="text-center text-sm font-medium text-text-tertiary py-2"
            key={day}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1 flex-1">
        {days.map((date, index) => (
          <div className="flex" key={index}>
            {date && (
              <button
                className={`w-full rounded-lg text-sm font-medium transition-colors ${
                  isSelected(date)
                    ? "bg-purple-600 text-white"
                    : isToday(date)
                      ? "bg-purple-100 text-purple-600"
                      : "hover:bg-bg-secondary text-text-primary"
                }`}
                onClick={() => onDateSelect(date)}
              >
                {date.getDate()}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const handleSkip = () => {
  // Navigate to next step or dashboard
  console.log("Skip onboarding call");
};

export function OnboardingCallPage() {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date(2025, 6)); // July 2025
  const [selectedDuration, setSelectedDuration] = useState(30);
  const [selectedTime, setSelectedTime] = useState<null | string>(null);

  const handleSchedule = () => {
    if (selectedDate && selectedTime) {
      console.log("Schedule call:", {
        date: selectedDate,
        duration: selectedDuration,
        time: selectedTime,
      });
    }
  };

  const formatSelectedDate = () => {
    if (!selectedDate) return "";

    return selectedDate.toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-bg-secondary">
      <div className="flex flex-col items-center absolute top-0 left-0 right-0">
        <OnboardingProgressBar currentStep={3} totalSteps={3} />

        <h2 className="mt-8 font-regular text-text-tertiary text-center text-[20px] sm:text-[24px]">
          Welcome to NoLimit Creatives! <br /> Get started in 3 easy steps:
        </h2>
      </div>

      <div className="grid h-screen place-items-center">
        <div className="mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            {/* Left Panel - Onboarding Call Info */}
            <div className="bg-white rounded-2xl border border-border-secondary p-8 max-w-[370px] h-full">
              <div className="flex items-center justify-center w-16 h-16 bg-purple-100 rounded-2xl mb-6">
                <svg
                  className="w-8 h-8 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                  />
                </svg>
              </div>

              <h2 className="text-2xl font-bold text-text-primary mb-4">
                Onboarding call
              </h2>

              <p className="text-text-secondary mb-6">
                Set yourself up for success with a quick onboarding call
              </p>

              <div className="text-sm text-text-tertiary space-y-4">
                <p>
                  Booking an onboarding call ensures you get the most out of
                  your design subscription by learning how to submit briefs
                  effectively and achieve the best results. This quick session
                  sets you up for success, helping streamline your workflow and
                  maximize the impact of every design project.
                </p>

                <p>
                  See{" "}
                  <a
                    className="text-purple-600 hover:underline"
                    href="https://help.nlc.com/en/articles/10750963-onboarding-at-nlc-everything-you-need-to-know"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    everything you need to know
                  </a>{" "}
                  about onboarding with us.
                </p>
              </div>
            </div>

            {/* Center Panel - Calendar */}
            <Calendar
              currentMonth={currentMonth}
              onDateSelect={setSelectedDate}
              onMonthChange={setCurrentMonth}
              selectedDate={selectedDate}
            />

            {/* Right Panel - Time Selection */}
            <div className="bg-white rounded-2xl border border-border-secondary p-8 h-full">
              {/* Meeting Duration */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-text-primary mb-4">
                  Meeting duration
                </h3>

                <select
                  className="w-full p-3 border border-border-secondary rounded-lg bg-white text-text-primary"
                  onChange={(e) => setSelectedDuration(Number(e.target.value))}
                  value={selectedDuration}
                >
                  {DURATIONS.map((duration) => (
                    <option key={duration.value} value={duration.value}>
                      {duration.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Time Selection */}
              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-2">
                  What time works best for you?
                </h3>

                {selectedDate && (
                  <p className="text-sm text-text-tertiary mb-4">
                    Showing available times for {formatSelectedDate()}
                  </p>
                )}

                <div className="mb-4">
                  <select className="w-full p-3 border border-border-secondary rounded-lg bg-white text-text-primary">
                    <option>UTC -3:00 Argentina, Brasil</option>
                  </select>
                </div>

                <div className="space-y-3 mb-6">
                  {AVAILABLE_TIMES.map((time) => (
                    <button
                      className={`w-full p-3 rounded-lg border text-center font-medium transition-colors ${
                        selectedTime === time
                          ? "bg-purple-600 text-white border-purple-600"
                          : "bg-white text-text-primary border-border-secondary hover:border-purple-600"
                      }`}
                      key={time}
                      onClick={() => setSelectedTime(time)}
                    >
                      {time}
                    </button>
                  ))}
                </div>

                {selectedDate && selectedTime && (
                  <Button
                    className="w-full"
                    onPress={handleSchedule}
                    variant="primary"
                  >
                    Schedule Call
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Skip Button - Outside the grid */}
          <div className="mt-4 flex justify-start">
            <Button
              className="max-w-[177px] bg-transparent"
              onPress={handleSkip}
              variant="secondary"
            >
              Skip for now
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
